"use client";

import { TriangleAlert } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useEffect, useId, useMemo, useState } from "react";

import { WayflyerLogo } from "@/components/nav/main-nav";
import { Input } from "@/components/ui/input";
import { SliderRoot, SliderThumb } from "@/components/ui/slider";
import { breakEvenVolumeUplift } from "@/lib/bf-toolkit/calculations";

import { DEFAULTS, RANGES, STRINGS } from "./strings";

interface DiscountBreakEvenProps {
  /** Request locale, used only for currency formatting. */
  locale: string;
  /** ISO currency code for Intl formatting (e.g. "USD"). */
  currency: string;
  /** Currency symbol for the input prefix (e.g. "$"). */
  currencySymbol: string;
}

// Bump the number text well above the Input default (text-heading-12) so the
// figures a founder types read clearly. twMerge resolves the font-size conflict.
const INPUT_TEXT = "text-heading-20";

const clamp = (value: number, min: number, max: number) =>
  Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min;

/**
 * Parse a money string the way a founder might type it: strip currency symbols,
 * commas and stray characters, keep digits and a single decimal point. Returns
 * NaN for an empty/unparseable field so the UI can show a neutral prompt rather
 * than a bogus result.
 */
function parseMoney(raw: string): number {
  const cleaned = raw.replace(/[^0-9.]/g, "");
  if (cleaned === "" || cleaned === ".") return Number.NaN;
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : Number.NaN;
}

/**
 * The required volume increase, phrased for the headline. Below 100% it reads
 * as a plain rounded percentage ("43% more units"); exactly doubling reads more
 * naturally in words ("twice as many units"); above that it switches to a
 * multiple, dropping a trailing ".0" ("2x more units", "3.5x more units").
 */
function upliftPhrase(uplift: number): string {
  const asPct = Math.round(uplift);
  if (uplift < 100) {
    if (asPct >= 100) return "twice as many units";
    return `${asPct}% more units`;
  }
  const mult = Math.round((uplift / 100) * 10) / 10;
  if (mult === 1) return "twice as many units";
  const multStr = Number.isInteger(mult) ? String(mult) : mult.toFixed(1);
  return `${multStr}x more units`;
}

/** Format a multiplier, dropping any trailing zeros (2.00 → "2", 4.50 → "4.5"). */
function trimZeros(n: number): string {
  return String(Number.parseFloat(n.toFixed(2)));
}

export function DiscountBreakEven({
  locale,
  currency,
  currencySymbol,
}: DiscountBreakEvenProps) {
  const [priceStr, setPriceStr] = useState<string>(String(DEFAULTS.price));
  const [costStr, setCostStr] = useState<string>(String(DEFAULTS.cost));
  const [discountPct, setDiscountPct] = useState<number>(DEFAULTS.discountPct);

  const price = parseMoney(priceStr);
  const cost = parseMoney(costStr);

  const formatCurrency = useMemo(() => {
    const nf = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    });
    return (value: number) => nf.format(value);
  }, [locale, currency]);

  // Which message the result panel shows.
  const state = useMemo(() => {
    if (!Number.isFinite(price) || !Number.isFinite(cost) || price <= 0) {
      return { kind: "empty" as const };
    }
    const discountedPrice = price * (1 - discountPct / 100);
    const marginAfter = discountedPrice - cost;
    // Selling at or below cost — covers both a discount too deep to stay
    // profitable and a cost that's already at/above the price. There's no
    // break-even volume here, so we detail the loss instead.
    if (marginAfter <= 0) {
      return {
        kind: "loss" as const,
        discountedPrice,
        cost,
        lossPerUnit: cost - discountedPrice,
        baselineLoss: cost >= price,
      };
    }
    // No discount means no change to margin, so nothing to make up on volume.
    if (discountPct === 0) {
      return { kind: "no-discount" as const, marginBefore: price - cost };
    }
    const uplift = breakEvenVolumeUplift(price, cost, discountPct);
    return {
      kind: "ok" as const,
      uplift,
      discountedPrice,
      marginBefore: price - cost,
      marginAfter,
      multiplier: 1 + uplift / 100,
    };
  }, [price, cost, discountPct]);

  // Fire one analytics event ~800ms after the user stops adjusting, and only
  // when there's a real result. Raw price/cost are intentionally not sent.
  const posthog = usePostHog();
  const upliftForAnalytics = state.kind === "ok" ? state.uplift : null;
  useEffect(() => {
    if (upliftForAnalytics === null) return;
    const timer = setTimeout(() => {
      posthog?.capture("bf_tool_calculated", {
        tool: "discount-break-even",
        discountPct,
        upliftPct: Math.round(upliftForAnalytics * 10) / 10,
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [posthog, discountPct, upliftForAnalytics]);

  const isLoss = state.kind === "loss";

  return (
    <div className="border-grey-100 bg-surface relative rounded-2xl border p-5 sm:p-7">
      <div className="grid gap-6 lg:grid-cols-2 lg:gap-10">
        {/* Inputs */}
        <div className="flex flex-col gap-5">
          <h2 className="text-heading-16 font-semibold text-[#0D0D0D]">
            {STRINGS.toolTitle}
          </h2>
          <MoneyField
            label={STRINGS.price}
            value={priceStr}
            symbol={currencySymbol}
            onChange={setPriceStr}
          />
          <MoneyField
            label={STRINGS.cost}
            value={costStr}
            symbol={currencySymbol}
            onChange={setCostStr}
          />
          <DiscountField value={discountPct} onChange={setDiscountPct} />
        </div>

        {/* Output */}
        <div aria-live="polite" className={cnPanel(isLoss)}>
          {state.kind === "empty" && (
            <p className="text-body-16 text-text-label">
              Enter your price and unit cost to see your break-even.
            </p>
          )}

          {state.kind === "no-discount" && (
            <p className="text-heading-24 lg:text-heading-32 text-[#0D0D0D]">
              With no discount you keep your full{" "}
              <span className="text-pulse-700">
                {formatCurrency(state.marginBefore)} margin per unit
              </span>
              , so there&rsquo;s no extra volume to make up. Add a discount to
              see your break-even.
            </p>
          )}

          {state.kind === "loss" && (
            <div>
              <div className="flex items-start gap-3">
                <TriangleAlert
                  className="mt-1 size-6 shrink-0 text-red-600"
                  aria-hidden="true"
                />
                <p className="text-heading-24 lg:text-heading-32 text-red-700">
                  {state.lossPerUnit > 0 ? (
                    <>
                      You&rsquo;d lose {formatCurrency(state.lossPerUnit)} on
                      every unit sold.
                    </>
                  ) : (
                    <>You&rsquo;d make no margin on every unit sold.</>
                  )}
                </p>
              </div>
              <p className="text-body-16 mt-3 text-red-700">
                {state.baselineLoss
                  ? `Your unit cost (${formatCurrency(state.cost)}) is at or above your selling price, so there's no margin to protect.`
                  : `At ${discountPct}% off you'd sell each unit for ${formatCurrency(state.discountedPrice)}, below your ${formatCurrency(state.cost)} cost.`}
              </p>
              <dl className="mt-6 flex flex-col gap-3 border-t border-red-200 pt-5">
                <SupportRow
                  label={STRINGS.discountedPrice}
                  value={formatCurrency(state.discountedPrice)}
                />
                <SupportRow
                  label={STRINGS.cost}
                  value={formatCurrency(state.cost)}
                />
                <SupportRow
                  label={STRINGS.lossPerUnit}
                  value={
                    state.lossPerUnit > 0
                      ? `-${formatCurrency(state.lossPerUnit)}`
                      : formatCurrency(0)
                  }
                  critical
                />
              </dl>
            </div>
          )}

          {state.kind === "ok" && (
            <div>
              <p className="text-heading-24 lg:text-heading-32 text-[#0D0D0D]">
                At {discountPct}% off, you need to sell
                {/* Force a break here so the varying quantity phrase always
                    starts a fresh line — keeps the headline from jumping as the
                    figure changes (e.g. "71% more units" vs "4x more units"). */}
                <br />
                <span className="text-pulse-700">
                  {upliftPhrase(state.uplift)}
                </span>{" "}
                just to hold gross profit.
              </p>
              <dl className="border-grey-100 mt-6 flex flex-col gap-3 border-t pt-5">
                <SupportRow
                  label={STRINGS.discountedPrice}
                  value={formatCurrency(state.discountedPrice)}
                />
                <SupportRow
                  label={STRINGS.marginBefore}
                  value={formatCurrency(state.marginBefore)}
                />
                <SupportRow
                  label={STRINGS.marginAfter}
                  value={formatCurrency(state.marginAfter)}
                />
                <SupportRow
                  label={STRINGS.multiplier}
                  value={`×${trimZeros(state.multiplier)}`}
                />
              </dl>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <p className="text-body-14 text-text-label max-w-xl italic">
          {STRINGS.disclaimer}
        </p>
        <WayflyerLogo
          aria-hidden="true"
          className="h-5 w-auto shrink-0 md:h-6 md:w-auto"
        />
      </div>
    </div>
  );
}

// Result panel background: blue for a healthy break-even, red when the discount
// tips the product into a loss.
function cnPanel(isLoss: boolean): string {
  const base =
    "flex min-h-[18rem] flex-col justify-center rounded-xl p-5 sm:p-6";
  return `${base} ${isLoss ? "bg-red-50" : "bg-pulse-50"}`;
}

function SupportRow({
  label,
  value,
  critical,
}: {
  label: string;
  value: string;
  critical?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-body-14 text-text-label">{label}</dt>
      <dd
        className={`text-body-16 font-semibold ${critical ? "text-red-700" : "text-[#0D0D0D]"}`}
      >
        {value}
      </dd>
    </div>
  );
}

function MoneyField({
  label,
  value,
  symbol,
  onChange,
}: {
  label: string;
  value: string;
  symbol: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="text-body-sm mb-2 block font-semibold text-[#0D0D0D]"
      >
        {label}
      </label>
      <div className="relative">
        <span className="text-text-label text-heading-20 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
          {symbol}
        </span>
        <Input
          id={id}
          inputMode="decimal"
          aria-label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`h-14 pl-9 ${INPUT_TEXT}`}
        />
      </div>
    </div>
  );
}

function DiscountField({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  const id = useId();
  const { min, max, step } = RANGES.discount;
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <label
          htmlFor={id}
          className="text-body-sm font-semibold text-[#0D0D0D]"
        >
          {STRINGS.discount}
        </label>
        <div className="relative">
          <Input
            id={id}
            type="number"
            inputMode="numeric"
            aria-label={STRINGS.discount}
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(clamp(Number(e.target.value), min, max))}
            className={`h-14 w-24 [appearance:textfield] pr-8 text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${INPUT_TEXT}`}
          />
          <span className="text-text-label text-heading-20 pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
            %
          </span>
        </div>
      </div>
      <SliderRoot
        aria-label={STRINGS.discount}
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={(vals) => onChange(clamp(vals[0] ?? min, min, max))}
        rangeClassName="bg-pulse-700"
        className="py-2"
      >
        <SliderThumb className="bg-pulse-700 focus-visible:ring-pulse-500 size-4 cursor-pointer rounded-full" />
      </SliderRoot>
    </div>
  );
}
